/**
 * linkify — Safe URL-to-anchor renderer for user-generated text.
 *
 * Beta punch-list P1-3 (comments + posts): detect http/https URLs AND
 * bare domains (e.g. `workmetier.com`, `www.example.com`) and render
 * them as clickable anchors, leaving the rest of the text as plain-text
 * React children. Because we never build HTML strings and never call
 * `dangerouslySetInnerHTML`, arbitrary HTML/script the user types stays
 * as text and cannot execute.
 *
 * Usage:
 *   <div style={{whiteSpace: "pre-wrap", wordBreak: "break-word"}}>
 *     {linkify(comment.text)}
 *   </div>
 */
import { openExternal } from "./externalLink";

// Common TLD allowlist. We ONLY auto-link bare domains whose TLD is on
// this list so filenames like `readme.md`, `script.js`, `image.png`
// stay as plain text. Full http(s) URLs are still linked regardless of
// TLD via the first branch of the alternation.
const TLD_ALLOWLIST = [
  "com","org","net","io","co","app","dev","ai","xyz","tech","info",
  "biz","me","tv","us","gov","edu","mil","name","pro",
  "uk","ca","de","fr","jp","au","nz","in","nl","se","es","it","be",
  "ru","br","mx","pl","ch","ie","dk","no","fi","at","cz","kr","cn",
  "sg","hk","za","ar","cl","co.uk","co.nz","com.au","io","to","ly",
  "so","fm","nyc","la","sh","gg",
].join("|");

// Two branches:
//   1. Explicit http/https URLs — allow anything after `://` up to whitespace/quotes.
//   2. Bare domains — hostname must end in a TLD from the allowlist above,
//      with an optional path.
// Group 1 = boundary char preserved in the plain-text stream (start-of-string,
// whitespace, or open bracket). Group 2 = the URL/domain we anchor.
// No lookbehind — keeps compatibility with Safari <16.4 / older WebViews.
const URL_RE = new RegExp(
  "(^|[\\s(\\[<>\"'])" +
  "(" +
    "https?:\\/\\/[^\\s<>\"')\\]]+" +          // absolute URL
    "|" +
    "(?:www\\.)?" +                             // bare domain (optional www.)
    "(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\\.)+" +
    "(?:" + TLD_ALLOWLIST + ")" +
    "(?:\\/[^\\s<>\"')\\]]*)?" +                // optional path/query/fragment
  ")",
  "gi",
);
const TRAILING_PUNCT_RE = /[.,;:!?)\]]+$/;

function safeHref(raw) {
  try {
    // Bare domains need a scheme so URL() parses them.
    const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const u = new URL(withScheme);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    return u.toString();
  } catch {
    return null;
  }
}

function handleClick(url) {
  return (e) => {
    // On Capacitor native, route through the SFSafariViewController-backed
    // openExternal helper for a proper in-app system browser experience.
    if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) {
      e.preventDefault();
      openExternal(url);
    }
    // Web: let the normal target=_blank behavior run.
  };
}

export function linkify(text) {
  if (!text || typeof text !== "string") return text || "";
  const out = [];
  let lastIdx = 0;
  let m;
  URL_RE.lastIndex = 0;
  let key = 0;
  while ((m = URL_RE.exec(text)) !== null) {
    const prefix = m[1] || "";
    const captured = m[2];
    const urlStart = m.index + prefix.length;
    let matched = captured;
    // Strip trailing sentence punctuation from the anchor and put it back
    // into the plain-text stream so the URL looks clean.
    const tail = matched.match(TRAILING_PUNCT_RE);
    let trailing = "";
    if (tail) {
      trailing = tail[0];
      matched = matched.slice(0, matched.length - trailing.length);
    }
    const href = safeHref(matched);
    // Emit everything before the URL — including this iteration's prefix
    // char (whitespace/bracket) so we don't drop it.
    if (urlStart > lastIdx) out.push(text.slice(lastIdx, urlStart));
    if (href) {
      out.push(
        <a
          key={`u${key++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick(href)}
          style={{ color: "var(--gold, #d4a84b)", textDecoration: "underline", wordBreak: "break-all" }}
        >
          {matched}
        </a>
      );
    } else {
      // Not a valid URL — keep as plain text.
      out.push(matched);
    }
    if (trailing) out.push(trailing);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) out.push(text.slice(lastIdx));
  return out;
}

export default linkify;
